/**
 * UID-116 (ADR-0051) / UID-292 — the SINGLE source of truth for the RFQ
 * pricing-region quote variants and their money-safe derivations.
 *
 * This is the framework-agnostic consolidation (ADR-0001 §2 — no Fastify, no
 * React) of what were previously two hand-mirrored, byte-identical twins:
 *   - `apps/api/src/modules/rfq/domain.ts` (server: attaches variants to the
 *     priced answer + recomputes the cart-commit committed date), and
 *   - `apps/web/src/verticals/rfq/variant-pricing.ts` + `domain.ts` (client:
 *     previews a variant's derived price / committed date BEFORE the customer
 *     picks "Add to cart", since the server only re-derives on commit).
 * Both sides now consume THIS one definition (re-exported under their existing
 * names at each app boundary, UID-278's `custom-lead-time-tiers` precedent), so
 * a change here reaches both and the two can never drift again.
 *
 * Type design (UID-292): shared stays contract/framework-agnostic, so it does
 * NOT import the generated openapi `QuoteVariant`. {@link QuoteVariant} below is
 * a small STRUCTURAL interface carrying exactly the fields the logic and the
 * consumers use; it is structurally identical to the generated `QuoteVariant`
 * schema (api local interface + web `components['schemas']['QuoteVariant']`), so
 * each app can keep typing its re-export as its own generated/local variant.
 */

/**
 * A pricing-region alternative for a Quote: the SAME priced lines re-expressed
 * at a region-specific price `ratio` and lead-time RANGE. Presentation/selection
 * metadata alongside the priced lines — NOT a new line type. `region`/`id` are
 * the PRICING-basis axis, deliberately DISTINCT from the ADR-0042 `RfqRegion`
 * request-targeting enum (do not conflate the two). Structurally mirrors the
 * frozen `QuoteVariant` contract schema.
 */
export interface QuoteVariant {
  /** Stable, order-significant selector the client echoes back at cart-commit. */
  readonly id: 'us' | 'offshore' | 'offshore_economy' | 'prefer_us_blend'
  /** Human pricing-region label (NOT the ADR-0042 RfqRegion enum). */
  readonly region: string
  /** Concise selector label for the variant chooser. */
  readonly label: string
  /** Decimal-STRING multiplier on each base line's Price.amount (ADR-0004: money
   * is exact — never a JS float). Applied by {@link applyVariantRatio}. */
  readonly ratio: string
  readonly leadTimeDaysMin: number
  readonly leadTimeDaysMax: number
  /** Presentation range, e.g. "5–7 business days" (en-dash). */
  readonly leadTimeLabel: string
  readonly note?: string
}

/**
 * UID-116 (ADR-0051 §5) — the canonical pricing-region variants both quoting
 * paths attach (auto-quote-policy.ts + cpq-writeback.ts) and the client's
 * pre-quote preview reads. ORDER IS SIGNIFICANT: us → offshore → offshore_economy
 * → prefer_us_blend (the presentation order the variant chooser renders; the two
 * offshore_* ids sit adjacent so the "Overseas" display column, UID-440, lists them
 * together). Ratios / lead ranges /
 * notes come from the Fictiv Platform `makeRfqVariants()` prototype — EXCEPT
 * `prefer_us_blend.ratio`, which UID-622 (ADR-0181 D3) lowered from the prototype's
 * '1.12' to '0.92' so the lead-time/price ladder is monotone (longer lead time =
 * cheaper) inside North America as well as Overseas; the US
 * variant (ratio "1.00") is the identity basis — its prices ARE the base lines,
 * never recomputed. ADR-0051 §Consequences: the ratio scales base LINE prices
 * only; ChargeLine add-ons (NRE/tooling) are flat and region-independent and are
 * carried through UNCHANGED — never ratio-scaled.
 */
export const RFQ_QUOTE_VARIANTS: readonly QuoteVariant[] = [
  {
    id: 'us',
    region: 'US region',
    label: 'US',
    ratio: '1.00',
    leadTimeDaysMin: 5,
    leadTimeDaysMax: 7,
    leadTimeLabel: '5–7 business days',
    note: 'Domestic sourcing — fastest turnaround, quoted to your drawing spec.',
  },
  {
    id: 'offshore',
    region: 'Asia region',
    label: 'Asia (offshore)',
    ratio: '0.78',
    leadTimeDaysMin: 10,
    leadTimeDaysMax: 14,
    leadTimeLabel: '10–14 business days',
    note: 'Offshore sourcing — lowest unit cost, longer lead time.',
  },
  {
    id: 'offshore_economy',
    region: 'Asia region economy',
    label: 'Asia economy',
    ratio: '0.65',
    leadTimeDaysMin: 18,
    leadTimeDaysMax: 22,
    leadTimeLabel: '18–22 business days',
    note: 'Offshore economy sourcing — lowest unit cost of all options, longest lead time.',
  },
  {
    id: 'prefer_us_blend',
    region: 'Prefer-US blend',
    label: 'Prefer-US blend',
    // UID-622 (ADR-0181 D3) — was '1.12' (a +12% premium over the `us` basis), which put
    // the LONGER 6–8 day North America option ABOVE the shorter 5–7 day one and broke the
    // ladder monotonicity Overseas already satisfied. Repriced BELOW '1.00' so the full
    // ladder reads 1.00 → 0.92 → 0.78 → 0.65 (see the monotonicity invariant in
    // rfq-quote-variants.test.ts). The REJECTED alternative was swapping the two North
    // America lead-time LABELS instead of the price: that would falsify `us`'s documented
    // "fastest turnaround" claim and slow `committedDateForVariant`'s promise derivation
    // for the basis variant.
    ratio: '0.92',
    leadTimeDaysMin: 6,
    leadTimeDaysMax: 8,
    leadTimeLabel: '6–8 business days',
    note: 'US-first with offshore fallback where capacity allows.',
  },
]

/**
 * UID-116 (ADR-0051 §4) — money-safe multiply of two decimal STRINGS, rounded
 * HALF-UP to 2 minor units, returning a decimal string. Uses BigInt integer
 * arithmetic so there is no float drift (ADR-0004: a JS number multiply would
 * risk rounding). This scales a base LINE `Price.amount` by a variant `ratio`
 * ONLY — ChargeLine amounts are flat/region-independent and are never passed
 * through here (ADR-0051 §Consequences).
 *
 * Malformed/empty input COERCES to 0 (→ "0.00") and never throws. The
 * authoritative design's money formatter is `v => '$' + Number(v || 0).toFixed(2)`
 * (design_handoff .../Fictiv Platform.dc.html) — a missing amount renders "$0.00"
 * and there is NO designed error state for any price field. Because this helper
 * runs in the RFQ price-preview RENDER paths (RfqCard / RfqDetailSummaryRail /
 * review-domain), it must degrade the same way rather than throw and white-screen
 * the view. Server-side price INTEGRITY (rejecting an amount the server did not
 * author) is enforced separately on the cart-commit request path (UID-126) — not
 * by crashing a pure display helper.
 */
export function applyVariantRatio(amount: string, ratio: string): string {
  const a = parseDecimal(amount)
  const r = parseDecimal(ratio)
  return formatRoundedHalfUp(a.digits * r.digits, a.scale + r.scale, 2)
}

/** The unsigned decimal-string shape (matches the contract's money pattern). */
const DECIMAL_PATTERN = /^\d+(\.\d+)?$/

/**
 * Parse an unsigned decimal string to scaled-integer form. Anything not matching
 * {@link DECIMAL_PATTERN} — empty, signed, exponent, junk — COERCES to 0 (design
 * -aligned graceful degradation, mirroring the design's `Number(v || 0)`); it
 * never throws, so a malformed amount can never crash an RFQ price preview.
 */
function parseDecimal(value: string): { digits: bigint; scale: number } {
  if (!DECIMAL_PATTERN.test(value)) {
    return { digits: 0n, scale: 0 }
  }
  const [intPart, fracPart = ''] = value.split('.')
  return { digits: BigInt((intPart === '' ? '0' : intPart) + fracPart), scale: fracPart.length }
}

/** Round `digits / 10^scale` to `targetScale` decimals, half-up, as a string. */
function formatRoundedHalfUp(digits: bigint, scale: number, targetScale: number): string {
  let scaled: bigint
  if (scale <= targetScale) {
    scaled = digits * 10n ** BigInt(targetScale - scale)
  } else {
    const divisor = 10n ** BigInt(scale - targetScale)
    const quotient = digits / divisor
    const remainder = digits % divisor
    scaled = remainder * 2n >= divisor ? quotient + 1n : quotient
  }
  const asString = scaled.toString().padStart(targetScale + 1, '0')
  return `${asString.slice(0, -targetScale)}.${asString.slice(-targetScale)}`
}

/**
 * UID-116 (ADR-0051 §3) — the cart-commit committed-date recompute: `now +
 * leadTimeDaysMax` as a plain YYYY-MM-DD date (the conservative upper-bound
 * promise). PURE and exported so the server cart-commit, the client preview, and
 * tests share ONE formula; it does NOT change the base-line DeliveryPromise the
 * auto/CPQ paths already produce (those keep their own single committed date
 * until a variant is picked).
 */
export function committedDateForVariant(now: Date, variant: QuoteVariant): string {
  const committed = new Date(now.getTime() + variant.leadTimeDaysMax * 24 * 60 * 60 * 1000)
  return committed.toISOString().slice(0, 10)
}
